import {
  LdInputMessage,
  LdLoading,
  LdOption,
  LdPagination,
  LdRadio,
  LdTable,
  LdTableBody,
  LdTableCaption,
  LdTableCell,
  LdTableHead,
  LdTableRow,
  LdTableToolbar,
} from "@liquid/react";
import { DownloadExcelButton } from "@src/components";
import { OptionType, ResultTableCustomProps } from "@src/types";
import { JSX, useEffect, useMemo, useRef, useState } from "react";
import Highlighter from "react-highlight-words";
import { DownloadExcelOffTarget } from "../Download/DownloadOffTarget/DownloadExcelOffTarget";
import { TableFilter } from "../TableFilter";
import {
  StyledDiv,
  StyledSelect,
  StyledTableHeader,
  StyledTableLabel,
  StyledTableRow,
} from "./ResultTable.style";
import { CRIPSER_PATH, ROWS_PER_PAGE } from "@src/config";
import { StyledLabel } from "./ResultTable.style";
import { LdSelectCustomEvent } from "@emdgroup-liquid/liquid";
import useIframeHeight from "../IframeHeight/Iframe";

const optionsRowsPerPage = ROWS_PER_PAGE.map((data: string, index: number) => ({
  label: data,
  value: data,
}));

const ResultTableCustom = ({
  data,
  tableDef,
  enableDownload = false,
  offTargetDownload = false,
  enableFilter,
  summaryData,
  labelText,
  removeToolbar,
  selectedMode,
  selectedCBEType,
  pagination = true,
  loading,
  onSort,
  dataInPage,
  onPageChange,
  onPageSizeChange,
  pageSize,
  onSelect,
  onSelectAll,
  onPrimerDesignSelected,
  onRowMouseEnter,
  onRowMouseLeave,
  isGenomeSpecific = true,
}: ResultTableCustomProps) => {
  const [elements, setElements] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState(0);

  const [elementsInPage, setElementsInPage] = useState<any>([]);

  const { uniqueKey, multiple, single } = tableDef;

  useEffect(() => {
    setElements(data);
    if (pagination) {
      setElementsInPage(dataInPage);
    } else {
      setElementsInPage(data);
    }
  }, [dataInPage, data, pagination, removeToolbar]);

  //Adding logic for height
  useIframeHeight(CRIPSER_PATH);

  const totalSelected = useMemo(
    () => elements?.filter((e: any) => e.selected).length,
    [elements]
  );

  const allSelected = useMemo(
    () => totalSelected === elements?.length,
    [elements, totalSelected]
  );

  const someSelected = useMemo(
    () => totalSelected > 0 && totalSelected < elements?.length,
    [elements, totalSelected]
  );

  const totalPages = useMemo(
    () => Math.ceil(elements?.length / pageSize) || Infinity,
    [elements, pageSize]
  );

  const tableRef = useRef<any>();

  function onPageChangeEvent(ev: any) {
    setCurrentPage(ev.detail);
    if (onPageChange) {
      onPageChange(ev.detail);
    }
  }

  useEffect(() => {
    const currentRef = tableRef.current;
    if (!currentRef) return;

    currentRef.addEventListener("ldTableSort", onSort, true);

    return () => {
      currentRef.removeEventListener("ldTableSort", onSort, true);
    };
  }, [onSort]);

  useEffect(() => {
    const current = tableRef.current;
    if (!current) return;
    current.addEventListener("ldTableSort", onSort, true);

    return () => {
      current.removeEventListener("ldTableSort", onSort, true);
    };
  }, []);

  function renderColumns(element: any, index: number): JSX.Element {
    const rowKey = `${uniqueKey}-${index}`;
    return (
      <LdTableRow
        selectable={multiple}
        selected={element.selected}
        onLdTableSelect={onSelect}
        key={rowKey}
        onMouseEnter={() => onRowMouseEnter?.(element, index)}
        onMouseLeave={() => onRowMouseLeave?.(element, index)}
        style={{ cursor: onRowMouseEnter ? "pointer" : undefined }}
      >
        {single ? (
          <LdTableCell key={`radio-${index}`}>
            <LdRadio
              checked={element.selected}
              value={element[uniqueKey]}
              name="radio"
              onLdchange={onSelect}
            />
          </LdTableCell>
        ) : undefined}
        {tableDef?.columns.map((col: any, cIndex: number) => {
          if (col.children && col.children.length) {
            return col.children.map((col2: any, cIndex2: number) => {
              return (
                <LdTableCell key={`${rowKey}-${cIndex}-${cIndex2}`}>
                  {col2.render && typeof col2.render === "function"
                    ? col2.render(element[col2.dataKey], element, index)
                    : element[col2.dataKey]}
                </LdTableCell>
              );
            });
          }

          if (col.render && typeof col.render === "function") {
            return (
              <LdTableCell key={`${rowKey}-${cIndex}`}>
                {col.render(element[col.dataKey], element)}
              </LdTableCell>
            );
          } else {
            return (
              <LdTableCell key={`${rowKey}-${cIndex}`}>
                {col.dataKey2 ? (
                  <>
                    <pre>{element[col.dataKey]}</pre>
                    <pre>{element[col.dataKey2]}</pre>
                  </>
                ) : col.dataKey === "transcript_status" ? (
                  <Highlighter
                    highlightStyle={{
                      color: "green",
                      backgroundColor: "transparent",
                    }}
                    searchWords={["Mane Select"]}
                    autoEscape={true}
                    textToHighlight={element[col.dataKey]}
                  />
                ) : (
                  element[col.dataKey]
                )}
              </LdTableCell>
            );
          }
        })}
      </LdTableRow>
    );
  }

  return (
    <>
      {labelText && <StyledTableLabel>{labelText}</StyledTableLabel>}
      <LdTable ref={tableRef} style={{ maxHeight: "36rem" }}>
        {!removeToolbar && (
          <LdTableToolbar slot="toolbar">
            {tableDef.tableTitle && (
              <LdTableCaption>{tableDef.tableTitle}</LdTableCaption>
            )}
            {totalPages && pagination && (
              <>
                <ShowPageList
                  onRowsPerPageChange={onPageSizeChange}
                  pageSize={pageSize}
                />
                <LdPagination
                  offset={1}
                  size="sm"
                  length={totalPages}
                  selectedIndex={currentPage}
                  onLdchange={onPageChangeEvent}
                />
              </>
            )}

            {enableFilter ? (
              <TableFilter
                label={enableFilter.label}
                onSelect={enableFilter.onSelect}
                options={enableFilter.options}
              />
            ) : undefined}
            {enableDownload && (
              <DownloadExcelButton
                data={elements}
                selectedMode={selectedMode}
                selectedCBEType={selectedCBEType}
                onPrimerDesignSelected={onPrimerDesignSelected}
                isGenomeSpecific={isGenomeSpecific}
              />
            )}
            {offTargetDownload && (
              <DownloadExcelOffTarget
                data={[data]}
                summaryData={[summaryData]}
              />
            )}
          </LdTableToolbar>
        )}

        <LdTableHead>
          <StyledTableRow
            selectable={multiple}
            selected={allSelected}
            indeterminate={multiple && someSelected}
            onLdTableSelectAll={onSelectAll}
          >
            {single ? <StyledTableHeader></StyledTableHeader> : undefined}
            {tableDef?.columns.map((col: any, index: number) => {
              return (
                <StyledTableHeader
                  scope={col.children ? "centeralign" : ""}
                  rowspan={col.children ? 1 : 2}
                  colspan={col.children ? 4 : 1}
                  key={`${index}-${col.key}`}
                  sortable={col.sortable || false}
                >
                  {col.name}
                </StyledTableHeader>
              );
            })}
          </StyledTableRow>
          <StyledTableRow scope="down" selectable={multiple}>
            {tableDef?.columns.map((col: any, index: number) => {
              return col?.children
                ? col?.children.map((col2: any, index2: number) => {
                  return (
                    <StyledTableHeader
                      scope="down"
                      key={`${index}-${col2.key}`}
                      sortable={col2.sortable || false}
                      className={`parent-data-index-${index}`}
                    >
                      {col2.name}
                    </StyledTableHeader>
                  );
                })
                : undefined;
            })}
          </StyledTableRow>
        </LdTableHead>
        <LdTableBody>
          {elementsInPage?.length > 0 ? (
            elementsInPage.map((element: any, index: number) =>
              renderColumns(element, index)
            )
          ) : loading ? (
            <LdTableRow>
              <LdTableCell colspan={4} style={{ textAlign: "center" }}>
                <LdLoading />
              </LdTableCell>
            </LdTableRow>
          ) : (
            <LdTableRow>
              <LdTableCell colspan={4} style={{ textAlign: "center" }}>
                <LdInputMessage>
                  <strong>Data not found</strong>
                </LdInputMessage>
              </LdTableCell>
            </LdTableRow>
          )}
        </LdTableBody>
      </LdTable>
    </>
  );
};

export { ResultTableCustom };

const ShowPageList = ({
  onRowsPerPageChange,
  pageSize,
}: {
  onRowsPerPageChange: ((pageSize: number) => void) | undefined;
  pageSize: number;
}) => {
  return (
    <StyledDiv>
      <StyledLabel>Rows Per Page</StyledLabel>
      <StyledSelect
        width="auto"
        preventDeselection
        onLdchange={(ev: LdSelectCustomEvent<any[]>) => {
          if (onRowsPerPageChange) {
            onRowsPerPageChange(Number(ev?.detail[0]));
          }
        }}
      >
        {optionsRowsPerPage.map((option: OptionType) => (
          <LdOption
            key={option.value}
            value={option.value}
            selected={Number(option.value) === pageSize}
            disabled={option.disabled}
          >
            {option.label}
          </LdOption>
        ))}
      </StyledSelect>
    </StyledDiv>
  );
};
